import { formatDistanceToNow } from "date-fns"
import type { IconName } from "hived-theme/lib/iconList"

import Icon from "@/components/Icon"
import { INVERS_VEHICLE_URL, ZERITY_VEHICLE_URL } from "@/constants/vehicles"
import type { Vehicle } from "@/hooks/useVehicles"
import { cn } from "@/lib/utils"

// A provider with no live row gets no link rather than a dead one.
export function ExternalLinksCell({
  zerityReference,
  inversReference,
}: {
  zerityReference: string | null
  inversReference: string | null
}) {
  const links = [
    {
      label: "Zerity",
      href: zerityReference && `${ZERITY_VEHICLE_URL}/${zerityReference}`,
    },
    {
      label: "Keyless",
      href: inversReference && `${INVERS_VEHICLE_URL}/${inversReference}`,
    },
  ].filter((link) => link.href)

  if (links.length === 0) return <span className="text-palette-jungleGreen500">-</span>

  return (
    <div className="flex items-center gap-2">
      {links.map((link) => (
        <a
          key={link.label}
          href={link.href as string}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-1 text-sm text-palette-blueCrayola500 hover:underline"
        >
          {link.label}
          <Icon name="arrowUpRight" className="h-3 w-3 text-current" />
        </a>
      ))}
    </div>
  )
}

export function DrivingCell({
  lockState,
  drivingEnabled,
}: {
  lockState: Vehicle["lock_state"]
  drivingEnabled: Vehicle["driving_enabled"]
}) {
  if (drivingEnabled === null) {
    return <span className="text-palette-jungleGreen500 italic">Unknown</span>
  }

  // Only the settled states expect anything; a van mid-command may legitimately be either.
  let mismatched = false
  if (lockState === "locked") {
    mismatched = drivingEnabled
  } else if (lockState === "unlocked") {
    mismatched = !drivingEnabled
  }

  return (
    <span className={mismatched ? "text-palette-honeycomb800" : undefined}>
      {drivingEnabled ? "Enabled" : "Disabled"}
    </span>
  )
}

export function LockStateCell({ lockState }: { lockState: Vehicle["lock_state"] }) {
  if (!lockState || lockState === "unknown") {
    return <span className="text-palette-jungleGreen500 italic">Unknown</span>
  }

  return (
    <span
      className={cn(
        "inline-flex h-5 w-fit items-center gap-1 rounded-full px-2 text-xs font-medium capitalize",
        lockState === "unlocked"
          ? "bg-palette-honeycomb100 text-palette-honeycomb800"
          : "bg-palette-jungleGreen100 text-palette-jungleGreen700",
      )}
    >
      <Icon
        name={lockState === "unlocked" ? "lockOpen" : "lock"}
        className="h-4 w-4 shrink-0 text-current [&_circle]:fill-current"
      />
      {lockState}
    </span>
  )
}

export function BatteryCell({
  batteryPercent,
  chargingStatus,
  // The detail panel shows charging on its own row, so the caption would just repeat it.
  showChargingCaption = true,
}: {
  batteryPercent: Vehicle["battery_percent"]
  chargingStatus: Vehicle["charging_status"]
  showChargingCaption?: boolean
}) {
  if (batteryPercent === null) {
    return <span className="text-palette-jungleGreen500 italic">-</span>
  }

  const isCharging = chargingStatus === "charging"
  const isPluggedIdle = chargingStatus === "not_charging_connected"

  // Being plugged in decides the colour outright; only a battery on its own is read by level.
  let tone = "text-palette-jungleGreen900"
  let batteryIcon: IconName = "batteryFull"
  if (isCharging) {
    batteryIcon = "batteryChargingVertical"
    // The palette's only green is too light to stay legible on white at this size.
    tone = "text-indicators-green"
  } else if (isPluggedIdle) {
    batteryIcon = "batteryChargingVertical"
    tone = "text-indicators-red"
  } else if (batteryPercent < 20) {
    batteryIcon = "batteryLow"
    tone = "text-indicators-red"
  } else if (batteryPercent < 50) {
    batteryIcon = "batteryHigh"
    tone = "text-palette-honeycomb700"
  }

  return (
    <div className={cn("flex w-fit flex-col gap-0.5", tone)}>
      <span className="flex items-center gap-1.5 text-sm">
        <Icon name={batteryIcon} className="h-4 w-4 shrink-0 text-current" />
        {batteryPercent}%
      </span>
      {/* Plugged in but not charging is the case ops have to go and look at. */}
      {showChargingCaption && isPluggedIdle && (
        <span className="text-[0.625rem]">Plugged in, not charging</span>
      )}
      {showChargingCaption && isCharging && <span className="text-[0.625rem]">Charging</span>}
    </div>
  )
}

// The detail panel gives charging its own row; the table keeps it inline under the battery.
export function ChargingCell({ chargingStatus }: { chargingStatus: Vehicle["charging_status"] }) {
  if (chargingStatus === "charging") {
    return <span className="text-indicators-green">Charging</span>
  }
  if (chargingStatus === "not_charging_connected") {
    // Plugged in but not charging is the case ops have to go and look at.
    return <span className="text-indicators-red">Plugged in, not charging</span>
  }
  if (chargingStatus === null) {
    return <span className="text-palette-jungleGreen500 italic">-</span>
  }
  return <span>Not plugged in</span>
}

export function StateAgeCell({ observedAt }: { observedAt: string | null }) {
  if (!observedAt) {
    return <span className="text-palette-jungleGreen500 italic">No state yet</span>
  }

  const observed = new Date(observedAt)

  return (
    <span className="text-palette-jungleGreen900" title={observed.toLocaleString()}>
      {formatDistanceToNow(observed, { addSuffix: true })}
    </span>
  )
}
